// Modules
import React, { createContext, useState, useContext } from 'react'

// Application
import { KEEP_LOGGED_LS_KEY, LIVIA_LS_KEY } from 'constants/app'
import { TokenPayload, AuthState, LoginType, Lab, KeepLoggedPayload } from 'typings/auth'
import { useLoginHook } from 'hooks/useLoginHook'
import { clearAllNavStorage } from 'utils'
import { EMPTY_DATA_USER } from 'constants/singin'

interface ContextData {
    state: {
        labs: Lab[]
        identifier: LoginType
        hasMounted: boolean
        isSubmitting: boolean
        authState: AuthState
    }
    operations: {
        setIdentifier(value: LoginType): void
        getAuthData(): AuthState | null
        setData(data: AuthState): void
        emptyData(): void
        clearStorage(): void
        addPayloadLS(data: AuthState, limitedFlow?: boolean): void
        addPayloadTokenLS(token: TokenPayload): void
        addPayloadKeepLoggedLS(token: KeepLoggedPayload): void
        signOut(): void
        setMounted(mounted: boolean): void
        setLabs(labs: Lab[]): void
        setSubmitting(submitting: boolean): void
    }
}

const AuthContext = createContext<ContextData>({} as ContextData)

export const LoginProvider: React.FC = ({ children }) => {
    const [identifier, _setIdentifier] = useState<LoginType>(LoginType.empty)
    const [labs, _setLabs] = useState<Lab[]>([])
    const [hasMounted, _setMounted] = useState<boolean>(false)
    const [isSubmitting, _setSubmitting] = useState<boolean>(false)
    const [authData, _setData] = useState<AuthState>(() => {
        const storage = localStorage.getItem(LIVIA_LS_KEY)

        if (storage) {
            return {
                token: JSON.parse(storage).token,
                refreshToken: JSON.parse(storage).refreshToken,
                signed: JSON.parse(storage).signed,
                user: null,
                limitedFlow: JSON.parse(storage).limitedFlow,
                redesign: JSON.parse(storage).redesign,
                keepLogged: {
                    keepLogged: JSON.parse(storage)?.keepLogged?.keepLogged,
                    latitude: JSON.parse(storage)?.keepLogged?.latitude,
                    longitude: JSON.parse(storage)?.keepLogged?.longitude,
                    address: JSON.parse(storage)?.keepLogged?.address,
                    uuid: JSON.parse(storage)?.keepLogged?.uuid,
                    patientId: JSON.parse(storage)?.keepLogged?.patientId,
                    username: JSON.parse(storage)?.keepLogged?.username,
                },
            }
        }

        return EMPTY_DATA_USER
    })

    const emptyData = () => {
        _setData(EMPTY_DATA_USER)
    }

    const getAuthData: () => AuthState | null = () => {
        const liviaLsData = localStorage.getItem(LIVIA_LS_KEY)
        return liviaLsData ? JSON.parse(liviaLsData) : null
    }

    const addPayloadLS = (data: AuthState, limitedFlow: boolean = false) => {
        localStorage.setItem(
            LIVIA_LS_KEY,
            JSON.stringify({
                ...data,
                signed: !!data.token,
                limitedFlow,
            }),
        )
    }

    const addPayloadTokenLS = (token: TokenPayload) => {
        localStorage.setItem(LIVIA_LS_KEY, JSON.stringify(token))
    }

    const addPayloadKeepLoggedLS = (token: KeepLoggedPayload) => {
        localStorage.setItem(KEEP_LOGGED_LS_KEY, JSON.stringify(token))
    }

    const signOut = () => {
        setData(EMPTY_DATA_USER)
        localStorage.setItem(LIVIA_LS_KEY, JSON.stringify(EMPTY_DATA_USER))

        sessionStorage.removeItem('chat_session')
        sessionStorage.removeItem('@Livia/federalTaxId')

        clearAllNavStorage()
    }

    const setData = (data: AuthState) => {
        _setData(data)
    }

    const clearStorage = () => {
        localStorage.setItem(LIVIA_LS_KEY, JSON.stringify(EMPTY_DATA_USER))
    }

    const setSubmitting = (submitting: boolean) => {
        _setSubmitting(submitting)
    }

    const setMounted = (mounted: boolean) => {
        _setMounted(mounted)
    }

    const setIdentifier = (value: LoginType) => {
        _setIdentifier(value)
    }

    const setLabs = (value: Lab[]) => {
        _setLabs(value)
    }

    return (
        <AuthContext.Provider
            value={{
                state: {
                    labs,
                    identifier,
                    hasMounted,
                    isSubmitting,
                    authState: authData || {},
                },
                operations: {
                    setIdentifier,
                    setSubmitting,
                    setMounted,
                    getAuthData,
                    setData,
                    setLabs,
                    emptyData,
                    clearStorage,
                    addPayloadLS,
                    addPayloadTokenLS,
                    addPayloadKeepLoggedLS,
                    signOut,
                },
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export function useLoginContext(): ContextData {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useLoginContext value is undefined. Make sure you use the AuthProvider before using the context.')
    }
    return context
}

// TODO: Precisamos alterar isso assim que todos os times refatorarem para Profile
export { useLoginHook as useAuth }
export default LoginProvider
